import { GraphUtils } from '@helpers/graph.helper';
import { Sensor } from '../sensor/sensor';
import { Router } from '@angular/router';
import { UtilsHelper } from '@helpers/utils.helper';

export class SoilMoistureAttribute extends Sensor {
  public soilMoistureChart: any;
  public soilMoistureSensor: any[][][] = []; // [position][probeIndex][dataPoints]
  public iconsToDisplay: { icon: string, type: number }[] = [];

  constructor(router: Router, url: string, alertsActive: boolean) {
    super(router, url, alertsActive);
    this.soilMoistureChart = null;
  }

  private ensureArraySize(arr: any[][][], position: number): void {
    while (arr.length <= position) {
      arr.push([[], [], [], [], [], [], [], []]); // Hasta 8 sondas
    }
  }

  public prepareData(): void {
    const iconMap = new Map<string, { icon: string, type: number }>();

    for (const element of this.data) {
      const date = new Date(element.date);
      const timestamp = GraphUtils.getDateUtc(date);
      const position = element.position ?? 0;
      const sensorInfo = UtilsHelper.nameSensor(element.type);
      const icon = sensorInfo.icon;

      if (!iconMap.has(icon)) {
        iconMap.set(icon, {
          icon,
          type: element.type
        });
      }


      this.ensureArraySize(this.soilMoistureSensor, position);

      if (element.soilMoisture !== undefined) {
        this.soilMoistureSensor[position][0].push([timestamp, element.soilMoisture]);
      }
      if (element.soilMoisture1 !== undefined) {
        this.soilMoistureSensor[position][0].push([timestamp, element.soilMoisture1]);
      }
      if (element.soilMoisture2 !== undefined) {
        this.soilMoistureSensor[position][1].push([timestamp, element.soilMoisture2]);
      }
      if (element.soilMoisture3 !== undefined) {
        this.soilMoistureSensor[position][2].push([timestamp, element.soilMoisture3]);
      }
      if (element.soilMoisture4 !== undefined) {
        this.soilMoistureSensor[position][3].push([timestamp, element.soilMoisture4]);
      }
      if (element.soilMoisture5 !== undefined) {
        this.soilMoistureSensor[position][4].push([timestamp, element.soilMoisture5]);
      }
      if (element.soilMoisture6 !== undefined) {
        this.soilMoistureSensor[position][5].push([timestamp, element.soilMoisture6]);
      }
      if (element.soilMoisture7 !== undefined) {
        this.soilMoistureSensor[position][6].push([timestamp, element.soilMoisture7]);
      }
      if (element.soilMoisture8 !== undefined) {
        this.soilMoistureSensor[position][7].push([timestamp, element.soilMoisture8]);
      }
    }
    this.iconsToDisplay = Array.from(iconMap.values());

  }

  public setCharts(): void {
    const moistureSeries: any[] = [];

    this.soilMoistureSensor.forEach((probes, position) => {
      probes.forEach((data, probeIndex) => {
        if (data.length > 0) {
          const hasOnlySoilMoistureField = probes.filter(p => p.length > 0).length === 1;
          const name = hasOnlySoilMoistureField
            ? `S${position + 1}: Humedad`
            : `S${position + 1}: Humedad ${probeIndex + 1}`;

          moistureSeries.push({
            name,
            data
          });
        }
      });
    });

    this.soilMoistureChart = this.getConfigChar(
      'soilMoisture',
      moistureSeries,
      this.getRanges('soilMoisture'),
      this.getLimits('soilMoisture')
    );
  }


  private decodePosition(pos: number): [number, number] {
    const position = Math.floor((pos - 1) / 8);
    const probeIndex = (pos - 1) % 8;
    return [position, probeIndex];
  }

  public hasSoilMoistureSensor(pos: number): boolean {
    const [position, probeIndex] = this.decodePosition(pos);
    return this.soilMoistureSensor[position]?.[probeIndex]?.length > 0;
  }

  public getSoilMoistureSensor(pos: number): any {
    const [position, probeIndex] = this.decodePosition(pos);
    const data = this.soilMoistureSensor[position]?.[probeIndex];
    return data?.length > 0 ? data[data.length - 1][1] ?? null : null;
  }

  public static getVariables(): any[] {
    return [
      { title: 'Humedad del Suelo', value: 'soilMoisture' },
      { title: 'Humedad del Suelo 1', value: 'soilMoisture1' },
      { title: 'Humedad del Suelo 2', value: 'soilMoisture2' },
      { title: 'Humedad del Suelo 3', value: 'soilMoisture3' },
      { title: 'Humedad del Suelo 4', value: 'soilMoisture4' },
      { title: 'Humedad del Suelo 5', value: 'soilMoisture5' },
      { title: 'Humedad del Suelo 6', value: 'soilMoisture6' },
      { title: 'Humedad del Suelo 7', value: 'soilMoisture7' },
      { title: 'Humedad del Suelo 8', value: 'soilMoisture8' },
    ];
  }

  protected getLimits(property: string): any[] {
    const limits = [];

    if (property.startsWith('soilMoisture')) {
      const propertiesToCheck = [
        'soilMoisture',
        'soilMoisture1', 'soilMoisture2', 'soilMoisture3', 'soilMoisture4',
        'soilMoisture5', 'soilMoisture6', 'soilMoisture7', 'soilMoisture8'
      ];

      for (const prop of propertiesToCheck) {
        const foundLimits = super.getLimits(prop);
        limits.push(...foundLimits);
      }
    } else {
      return super.getLimits(property);
    }

    return limits;
  }

  protected getRanges(property: string): any[] {
    const ranges = [];

    if (property.startsWith('soilMoisture')) {
      const propertiesToCheck = [
        'soilMoisture',
        'soilMoisture1', 'soilMoisture2', 'soilMoisture3', 'soilMoisture4',
        'soilMoisture5', 'soilMoisture6', 'soilMoisture7', 'soilMoisture8'
      ];

      for (const prop of propertiesToCheck) {
        const foundRanges = super.getRanges(prop);
        ranges.push(...foundRanges);
      }
      return ranges;
    } else {
      return super.getRanges(property);
    }
  }
}
